// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Settings Page
// User settings and preferences management
// ============================================================================

import { useState } from 'react';
import {
  User,
  Bell,
  Shield,
  Palette,
  Keyboard,
  Monitor,
  Moon,
  Sun,
  Mail,
  Clock,
  Save,
  Check,
  Camera,
  Lock,
  Key,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/common/ui';
import { clsx } from 'clsx';

type SettingsTab = 'profile' | 'notifications' | 'security' | 'appearance' | 'shortcuts';

interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  label: string;
  description?: string;
}

function ToggleSwitch({ enabled, onChange, label, description }: ToggleSwitchProps) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium text-slate-900">{label}</p>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={clsx(
          'relative w-11 h-6 rounded-full transition-colors',
          enabled ? 'bg-pharmacy-primary' : 'bg-slate-200'
        )}
      >
        <span
          className={clsx(
            'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
            enabled && 'translate-x-5'
          )}
        />
      </button>
    </div>
  );
}

const TABS: { id: SettingsTab; label: string; icon: typeof User }[] = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'shortcuts', label: 'Shortcuts', icon: Keyboard },
];

export function SettingsPage() {
  const { user, isPharmacist } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [saved, setSaved] = useState(false);

  // Profile state
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('(555) 123-4567');

  // Notification settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [durAlerts, setDurAlerts] = useState(true);
  const [statOrders, setStatOrders] = useState(true);
  const [inventoryAlerts, setInventoryAlerts] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Security settings
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState('30');
  const [showPassword, setShowPassword] = useState(false);

  // Appearance settings
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('light');
  const [compactMode, setCompactMode] = useState(false);
  const [animationsEnabled, setAnimationsEnabled] = useState(true);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500 mt-1">Manage your account preferences</p>
        </div>
        <Button onClick={handleSave} className="gap-2">
          {saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Sidebar Tabs */}
        <div className="w-56 flex-shrink-0">
          <nav className="space-y-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left transition-colors',
                  activeTab === tab.id
                    ? 'bg-pharmacy-primary text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                )}
              >
                <tab.icon className="h-5 w-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Avatar */}
                  <div className="flex items-center gap-6">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full bg-pharmacy-primary text-white flex items-center justify-center text-3xl font-semibold">
                        {firstName?.[0]}{lastName?.[0]}
                      </div>
                      <button className="absolute bottom-0 right-0 w-8 h-8 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-sm hover:bg-slate-50 transition-colors">
                        <Camera className="h-4 w-4 text-slate-600" />
                      </button>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-slate-900">{firstName} {lastName}</p>
                      <p className="text-slate-500">{isPharmacist ? 'Pharmacist' : 'Technician'}</p>
                      <p className="text-sm text-slate-400 mt-1">Credentials on file</p>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pharmacy-primary/20 focus:border-pharmacy-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pharmacy-primary/20 focus:border-pharmacy-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pharmacy-primary/20 focus:border-pharmacy-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pharmacy-primary/20 focus:border-pharmacy-primary"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Professional Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        License Number
                      </label>
                      <input
                        type="text"
                        defaultValue="PH-123456"
                        disabled
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        NPI Number
                      </label>
                      <input
                        type="text"
                        defaultValue="9876543210"
                        disabled
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        DEA Number
                      </label>
                      <input
                        type="text"
                        defaultValue="AM1234567"
                        disabled
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        License Expiration
                      </label>
                      <input
                        type="text"
                        defaultValue="December 31, 2026"
                        disabled
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Notification Channels
                  </CardTitle>
                </CardHeader>
                <CardContent className="divide-y divide-slate-100">
                  <ToggleSwitch
                    enabled={emailNotifications}
                    onChange={setEmailNotifications}
                    label="Email Notifications"
                    description="Receive notifications via email"
                  />
                  <ToggleSwitch
                    enabled={smsNotifications}
                    onChange={setSmsNotifications}
                    label="SMS Notifications"
                    description="Receive text messages for urgent alerts"
                  />
                  <ToggleSwitch
                    enabled={pushNotifications}
                    onChange={setPushNotifications}
                    label="Push Notifications"
                    description="Browser push notifications"
                  />
                  <ToggleSwitch
                    enabled={soundEnabled}
                    onChange={setSoundEnabled}
                    label="Sound Alerts"
                    description="Play sound for new notifications"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5" />
                    Alert Types
                  </CardTitle>
                </CardHeader>
                <CardContent className="divide-y divide-slate-100">
                  <ToggleSwitch
                    enabled={durAlerts}
                    onChange={setDurAlerts}
                    label="DUR Alerts"
                    description="Drug utilization review alerts"
                  />
                  <ToggleSwitch
                    enabled={statOrders}
                    onChange={setStatOrders}
                    label="STAT Orders"
                    description="Urgent prescription notifications"
                  />
                  <ToggleSwitch
                    enabled={inventoryAlerts}
                    onChange={setInventoryAlerts}
                    label="Inventory Alerts"
                    description="Low stock and reorder notifications"
                  />
                </CardContent>
              </Card>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="h-5 w-5" />
                    Password
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter current password"
                        className="w-full px-4 py-2 pr-10 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pharmacy-primary/20 focus:border-pharmacy-primary"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        New Password
                      </label>
                      <input
                        type="password"
                        placeholder="Enter new password"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pharmacy-primary/20 focus:border-pharmacy-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        placeholder="Confirm new password"
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pharmacy-primary/20 focus:border-pharmacy-primary"
                      />
                    </div>
                  </div>
                  <Button variant="outline">Change Password</Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="h-5 w-5" />
                    Two-Factor Authentication
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ToggleSwitch
                    enabled={twoFactorEnabled}
                    onChange={setTwoFactorEnabled}
                    label="Enable 2FA"
                    description="Add an extra layer of security to your account"
                  />
                  {twoFactorEnabled && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">
                        Two-factor authentication is enabled. You'll receive a code via SMS when signing in.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Session Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Auto-logout after inactivity
                    </label>
                    <select
                      value={sessionTimeout}
                      onChange={(e) => setSessionTimeout(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pharmacy-primary/20 focus:border-pharmacy-primary"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="120">2 hours</option>
                    </select>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5" />
                    Theme
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { id: 'light', label: 'Light', icon: Sun },
                      { id: 'dark', label: 'Dark', icon: Moon },
                      { id: 'system', label: 'System', icon: Monitor },
                    ].map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setTheme(option.id as typeof theme)}
                        className={clsx(
                          'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all',
                          theme === option.id
                            ? 'border-pharmacy-primary bg-pharmacy-primary/5'
                            : 'border-slate-200 hover:border-slate-300'
                        )}
                      >
                        <option.icon
                          className={clsx(
                            'h-6 w-6',
                            theme === option.id ? 'text-pharmacy-primary' : 'text-slate-500'
                          )}
                        />
                        <span
                          className={clsx(
                            'text-sm font-medium',
                            theme === option.id ? 'text-pharmacy-primary' : 'text-slate-600'
                          )}
                        >
                          {option.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Monitor className="h-5 w-5" />
                    Display Options
                  </CardTitle>
                </CardHeader>
                <CardContent className="divide-y divide-slate-100">
                  <ToggleSwitch
                    enabled={compactMode}
                    onChange={setCompactMode}
                    label="Compact Mode"
                    description="Reduce spacing for more content on screen"
                  />
                  <ToggleSwitch
                    enabled={animationsEnabled}
                    onChange={setAnimationsEnabled}
                    label="Animations"
                    description="Enable smooth transitions and animations"
                  />
                </CardContent>
              </Card>
            </div>
          )}

          {/* Shortcuts Tab */}
          {activeTab === 'shortcuts' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Keyboard className="h-5 w-5" />
                  Keyboard Shortcuts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {[
                    {
                      title: 'Navigation',
                      shortcuts: [
                        { keys: ['G', 'D'], description: 'Go to Dashboard' },
                        { keys: ['G', 'V'], description: 'Go to Verification' },
                        { keys: ['G', 'F'], description: 'Go to Fill Queue' },
                        { keys: ['G', 'S'], description: 'Go to Settings' },
                      ],
                    },
                    {
                      title: 'Actions',
                      shortcuts: [
                        { keys: ['V'], description: 'Verify prescription' },
                        { keys: ['A'], description: 'Approve / Accept' },
                        { keys: ['X'], description: 'Reject' },
                        { keys: ['?'], description: 'Show shortcuts' },
                      ],
                    },
                  ].map((group) => (
                    <div key={group.title}>
                      <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
                        {group.title}
                      </h4>
                      <div className="space-y-2">
                        {group.shortcuts.map((shortcut, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50"
                          >
                            <span className="text-sm text-slate-600">{shortcut.description}</span>
                            <div className="flex items-center gap-1">
                              {shortcut.keys.map((key, keyIdx) => (
                                <span key={keyIdx} className="flex items-center gap-1">
                                  <kbd className="px-2 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded shadow-sm">
                                    {key}
                                  </kbd>
                                  {keyIdx < shortcut.keys.length - 1 && (
                                    <span className="text-slate-400 text-xs">+</span>
                                  )}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Tip:</strong> Press <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-blue-100 border border-blue-300 rounded">?</kbd> anywhere to view the full keyboard shortcuts reference.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

